/**
 * Color extraction utility for extracting dominant colors from images
 * and generating gradients based on those colors
 */

interface ColorInfo {
  r: number;
  g: number;
  b: number;
  hex: string;
}

interface GradientColors {
  primary: ColorInfo;
  secondary: ColorInfo;
  cssGradient: string;
}

/**
 * Convert RGB values to hex
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

/**
 * Calculate the luminance of a color to determine if it's light or dark
 */
const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Enhance color saturation and brightness for better gradient appearance
 */
const enhanceColor = (r: number, g: number, b: number, factor: number = 1.2): ColorInfo => {
  // Increase saturation
  const gray = 0.299 * r + 0.587 * g + 0.114 * b;
  const enhancedR = Math.min(255, Math.max(0, Math.round(gray + factor * (r - gray))));
  const enhancedG = Math.min(255, Math.max(0, Math.round(gray + factor * (g - gray))));
  const enhancedB = Math.min(255, Math.max(0, Math.round(gray + factor * (b - gray))));
  
  return {
    r: enhancedR,
    g: enhancedG,
    b: enhancedB,
    hex: rgbToHex(enhancedR, enhancedG, enhancedB)
  };
};

/**
 * Extract dominant colors from an image using canvas
 */
export const extractColorsFromImage = (imageSrc: string): Promise<GradientColors> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    // Set crossOrigin for external URLs, but not for our own cached thumbnails
    if (!imageSrc.includes('/api/thumbnails/cached/')) {
      img.crossOrigin = 'anonymous';
    }
    
    img.onload = () => {
      try {
        // console.log('[ColorExtractor] Image loaded successfully, dimensions:', img.width, 'x', img.height);
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error('Could not get canvas context');
        }
        
        // Resize image for faster processing
        const maxSize = 100;
        const ratio = Math.min(maxSize / img.width, maxSize / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        
        // Color frequency map
        const colorMap = new Map<string, { count: number; r: number; g: number; b: number }>();
        
        // Sample every nth pixel to improve performance
        const sampleRate = 4;
        for (let i = 0; i < pixels.length; i += 4 * sampleRate) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const a = pixels[i + 3];
          
          // Skip transparent pixels and very dark/light pixels
          if (a < 128) continue;
          
          const luminance = getLuminance(r, g, b);
          if (luminance < 0.05 || luminance > 0.95) continue;
          
          // Reduce color precision for better grouping
          const quantizedR = Math.round(r / 32) * 32;
          const quantizedG = Math.round(g / 32) * 32;
          const quantizedB = Math.round(b / 32) * 32;
          
          const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;
          
          if (colorMap.has(colorKey)) {
            colorMap.get(colorKey)!.count++;
          } else {
            colorMap.set(colorKey, { count: 1, r: quantizedR, g: quantizedG, b: quantizedB });
          }
        }
        
        // Get the most frequent colors
        const sortedColors = Array.from(colorMap.values())
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);
        
        if (sortedColors.length === 0) {
          // Fallback to default Twitch purple gradient
          resolve({
            primary: { r: 145, g: 70, b: 255, hex: '#9146ff' },
            secondary: { r: 106, g: 13, b: 173, hex: '#6a0dad' },
            cssGradient: 'from-purple-500 to-indigo-600'
          });
          return;
        }
        
        // Find two complementary colors for the gradient
        let primaryColor = sortedColors[0];
        let secondaryColor = sortedColors[1] || primaryColor;
        
        // If we have multiple colors, try to find ones with good contrast
        if (sortedColors.length > 1) {
          for (let i = 1; i < Math.min(sortedColors.length, 5); i++) {
            const candidate = sortedColors[i];
            const colorDistance = Math.sqrt(
              Math.pow(primaryColor.r - candidate.r, 2) +
              Math.pow(primaryColor.g - candidate.g, 2) +
              Math.pow(primaryColor.b - candidate.b, 2)
            );
            
            // If we find a color with good distance, use it
            if (colorDistance > 80) {
              secondaryColor = candidate;
              break;
            }
          }
        }
        
        // Enhance the colors for better visual appeal
        const enhancedPrimary = enhanceColor(primaryColor.r, primaryColor.g, primaryColor.b, 1.3);
        const enhancedSecondary = enhanceColor(secondaryColor.r, secondaryColor.g, secondaryColor.b, 1.1);
        
        // Generate CSS gradient classes based on the extracted colors
        const cssGradient = generateTailwindGradient(enhancedPrimary, enhancedSecondary);
        
        resolve({
          primary: enhancedPrimary,
          secondary: enhancedSecondary,
          cssGradient
        });
        
      } catch (error) {
        // console.warn('Error extracting colors from image:', error);
        // Fallback to default gradient
        resolve({
          primary: { r: 145, g: 70, b: 255, hex: '#9146ff' },
          secondary: { r: 106, g: 13, b: 173, hex: '#6a0dad' },
          cssGradient: 'from-purple-500 to-indigo-600'
        });
      }
    };
    
    img.onerror = (error) => {
      // console.warn('[ColorExtractor] Failed to load image for color extraction:', {
      //   url: imageSrc.substring(0, 100) + '...',
      //   error,
      //   crossOrigin: img.crossOrigin
      // });
      // Fallback to default gradient
      resolve({
        primary: { r: 145, g: 70, b: 255, hex: '#9146ff' },
        secondary: { r: 106, g: 13, b: 173, hex: '#6a0dad' },
        cssGradient: 'from-purple-500 to-indigo-600'
      });
    };
    
    img.src = imageSrc;
  });
};

/**
 * Generate appropriate Tailwind CSS gradient classes based on extracted colors
 */
const generateTailwindGradient = (primary: ColorInfo, secondary: ColorInfo): string => {
  // Map RGB values to closest Tailwind color
  const getTailwindColor = (r: number, g: number, b: number): string => {
    // Define common color ranges
    const hue = rgbToHsl(r, g, b)[0];
    
    if (r > g && r > b) {
      // Red dominant
      if (r > 200) return 'red-500';
      if (r > 150) return 'red-600';
      return 'red-700';
    } else if (g > r && g > b) {
      // Green dominant
      if (g > 200) return 'green-500';
      if (g > 150) return 'green-600';
      return 'green-700';
    } else if (b > r && b > g) {
      // Blue dominant
      if (hue > 220 && hue < 280) {
        // Purple range
        if (b > 200) return 'purple-500';
        if (b > 150) return 'purple-600';
        return 'purple-700';
      } else {
        // Blue range
        if (b > 200) return 'blue-500';
        if (b > 150) return 'blue-600';
        return 'blue-700';
      }
    } else {
      // Mixed colors
      const luminance = getLuminance(r, g, b);
      if (luminance > 0.6) return 'gray-400';
      if (luminance > 0.3) return 'gray-600';
      return 'gray-800';
    }
  };
  
  const primaryTailwind = getTailwindColor(primary.r, primary.g, primary.b);
  const secondaryTailwind = getTailwindColor(secondary.r, secondary.g, secondary.b);
  
  return `from-${primaryTailwind} to-${secondaryTailwind}`;
};

/**
 * Convert RGB to HSL
 */
const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  r /= 255;
  g /= 255;
  b /= 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  
  return [h * 360, s * 100, l * 100];
};

/**
 * Create a custom CSS gradient string for inline styles
 */
export const createCustomGradient = (colors: GradientColors): string => {
  return `linear-gradient(135deg, ${colors.primary.hex}, ${colors.secondary.hex})`;
};

/**
 * Cache for extracted colors to avoid re-processing the same images
 */
const colorCache = new Map<string, GradientColors>();

/**
 * Extract colors with caching
 */
export const extractColorsWithCache = async (imageSrc: string): Promise<GradientColors> => {
  if (colorCache.has(imageSrc)) {
    return colorCache.get(imageSrc)!;
  }
  
  const colors = await extractColorsFromImage(imageSrc);
  colorCache.set(imageSrc, colors);
  return colors;
};